import { Component, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { AuthService } from '../../services/auth.service';
import { ProfileService } from '../../services/profile.service';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { UserProfile } from '../../shared/types/user.interface';
import { NotificationService } from '../../services/notification.service'; // NotificationService;
import { Subscription } from 'rxjs';

interface CompanyProfile {
  id?: string;
  name?: string;
}

/**
 * ProfileDropdownComponent displays the current user's profile summary and quick actions (navigate to settings/profile and logout) in a compact dropdown UI.
 * @component
 * @selector app-profile-dropdown
 * @example
 *   <app-profile-dropdown></app-profile-dropdown>
 *
 * Purpose:
 *   - Subscribes to profile updates and exposes display helpers for the user's avatar, name, role, email, and phone.
 *   - Loads company details from `company/me` (via ProfileService.getBusinessProfile) for the dropdown.
 *   - Provides actions for navigating to settings/profile based on role and for logging out the user.
 *
 * Inputs:
 * @input none - This component does not accept any @Input properties; it sources data from ProfileService and AuthService.
 *
 * Outputs:
 * @output none - This component does not emit any @Output events; interaction results (e.g., logout) are handled internally and via injected services.
 *
 * Injected services:
 *   - AuthService: used to perform logout.
 *   - ProfileService: provides userProfile$ observable, lazy-loading, and current profile getter.
 *   - NotificationService: used to display feedback messages (e.g., logout success).
 *   - Router: used to navigate to settings or profile pages.
 *
 * Key public properties:
 *   - userProfile: UserProfile | null — holds the current user profile received from ProfileService.
 *   - isLoading: boolean — indicates whether profile data is being loaded.
 *
 * Important lifecycle hooks:
 *   - ngOnInit(): Subscribes to profile changes only; initial `user/me` is triggered by shell (Header/SideNav) via `loadUserProfileIfNeeded()`.
 *   - ngOnDestroy(): Cleans up subscriptions to prevent memory leaks.
 *
 * Key methods:
 *   - subscribeToProfileChanges(): Subscribes to profileService.userProfile$ and updates userProfile and isLoading accordingly.
 *   - loadUserProfile(): Calls profileService.loadUserProfileIfNeeded() to prevent duplicate API calls and sets isLoading based on current cached profile.
 *   - logout(): Calls authService.logout() and shows a success message via messageService.
 *   - getProfileImage(): Returns the user's picture URL when available or a default avatar path.
 *   - getDisplayName(): Returns the user's name or a fallback "User".
 *   - getDisplayRole(): Returns the user's role or a fallback "Role".
 *   - getDisplayPhone(): Returns the user's contact number or "N/A".
 *   - getDisplayEmail(): Returns the user's email or "N/A".
 *   - navigateToSettings(): Routes to '/profile' for staff users or '/settings' for other roles.
 *
 * Notes:
 *   - The component is standalone and imports CommonModule.
 *   - It centrally relies on ProfileService to avoid duplicate profile fetches and to receive updates via an observable stream.
 */
@Component({
  selector: 'app-profile-dropdown',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './profile-dropdown.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./profile-dropdown.component.scss'],
})
export class ProfileDropdownComponent implements OnInit, OnDestroy {
  userProfile: UserProfile | null = null;
  companyProfile: CompanyProfile | null = null;
  isLoading = true;
  private subscriptions: Subscription = new Subscription();

  constructor(
    private authService: AuthService,
    private profileService: ProfileService,
    private messageService: NotificationService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.subscribeToProfileChanges();
    this.loadCompanyProfile();
    // Profile is loaded by Header + SideNav via `ProfileService.loadUserProfileIfNeeded()` — avoid a redundant `user/me` call here.
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  subscribeToProfileChanges(): void {
    this.subscriptions.add(
      this.profileService.userProfile$.subscribe(profile => {
        this.userProfile = profile;
        // Set loading to false once we have profile data (or if it's null after initial load)
        if (profile !== null || this.isLoading) {
          this.isLoading = false;
        }
      })
    );
  }

  loadUserProfile(): void {
    // Use ProfileService to load profile if needed (prevents duplicate API calls)
    this.profileService.loadUserProfileIfNeeded();

    // If profile is already available, set loading to false immediately
    const currentProfile = this.profileService.getCurrentUserProfile();
    if (currentProfile !== null) {
      this.isLoading = false;
    }
  }

  logout(): void {
    this.authService.logout();
    this.messageService.add({
      severity: 'success',
      summary: 'You are logged out!',
      detail: 'You have successfully logged out of your account.',
      life: 3000,
    });
  }

  getProfileImage(): string {
    if (this.userProfile?.picture_url) {
      return this.userProfile.picture_url;
    }
    return '/assets/images/switch-account/Frame 1171276019.svg';
  }

  getDisplayName(): string {
    return this.userProfile?.name || 'User';
  }

  getDisplayRole(): string {
    return this.userProfile?.role || 'Role';
  }

  getDisplayPhone(): string {
    return this.userProfile?.contact_number || 'N/A';
  }

  getDisplayEmail(): string {
    return this.userProfile?.email || 'N/A';
  }

  loadCompanyProfile(): void {
    this.subscriptions.add(
      this.profileService.getBusinessProfile().subscribe({
        next: response => {
          if (response?.status && response.data) {
            this.companyProfile = response.data;
          }
        },
        error: () => {
          this.companyProfile = null;
        },
      })
    );
  }

  getCompanyName(): string {
    return this.companyProfile?.name || '';
  }

  navigateToSettings(): void {
    if (
      this.userProfile?.role?.toLowerCase() === 'attendant' ||
      this.userProfile?.role?.toLowerCase() === 'attendant_plus' ||
      this.userProfile?.role?.toLowerCase() === 'staff'
    ) {
      this.router.navigate(['/profile']);
    } else {
      this.router.navigate(['/settings']);
    }
  }
}
